import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaTicketAlt, FaComments, FaPaperPlane, FaCheck, FaUserCheck, FaTrash } from 'react-icons/fa';

const TicketDetailsModal = ({ ticket, onClose, userInfo, API_BASE_URL, auth, onUpdate }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolutionForm, setShowResolutionForm] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [entityExists, setEntityExists] = useState(null); // null = not checked, true = exists, false = doesn't exist


  const statusColors = {
    open: '#3498db',
    in_progress: '#f39c12',
    resolved: '#27ae60',
    closed: '#95a5a6',
    cancelled: '#e74c3c'
  };

  const priorityColors = {
    low: '#95a5a6',
    medium: '#3498db',
    high: '#f39c12',
    urgent: '#e74c3c'
  };

  useEffect(() => {
    if (ticket) {
      fetchComments();
      // Check if entity exists for delete request tickets
      if (isDeleteRequestTicket()) {
        checkEntityExists();
      }
    }
  }, [ticket]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments?include_internal=true`,
        auth
      );
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Check if the entity in a delete request ticket actually exists
  const checkEntityExists = async () => {
    const deleteInfo = parseDeleteRequestInfo();
    if (!deleteInfo || !deleteInfo.entityId || !deleteInfo.entityType) {
      setEntityExists(false);
      return;
    }

    try {
      const endpoint = deleteInfo.entityType === 'trader' ? 'traders' : 'zonal-groups';
      await axios.get(`${API_BASE_URL}/${endpoint}/${deleteInfo.entityId}`, auth);
      setEntityExists(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setEntityExists(false);
      } else {
        // For other errors, assume it exists but there's a different issue
        setEntityExists(true);
      }
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments`,
        {
          user_id: userInfo.user_id,
          comment: newComment,
          is_internal: isInternal
        },
        auth
      );

      setNewComment('');
      setIsInternal(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/resolve`,
        {
          resolved_by: userInfo.user_id,
          resolution_notes: resolutionNotes
        },
        auth
      );

      setShowResolutionForm(false);
      setResolutionNotes('');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error resolving ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  // Handle deletion of trader/group from delete request tickets
  const handleDeleteEntity = async () => {
    const deleteInfo = parseDeleteRequestInfo();
    if (!deleteInfo || !deleteInfo.entityId || !deleteInfo.entityType) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Invalid Delete Request',
        message: 'Unable to extract entity information from this ticket.'
      });
      return;
    }

    const confirmMessage = `Are you sure you want to permanently delete this ${deleteInfo.entityType}?\n\nEntity: ${deleteInfo.entityName}\nID: ${deleteInfo.entityId}\n\nThis action cannot be undone and will automatically resolve this ticket.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteLoading(true);
    try {
      // Delete the entity from the database
      const endpoint = deleteInfo.entityType === 'trader' ? 'traders' : 'zonal-groups';
      await axios.delete(
        `${API_BASE_URL}/${endpoint}/${deleteInfo.entityId}`,
        {
          ...auth,
          data: { user_id: userInfo.user_id } // Pass user ID for ticket resolution
        }
      );

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Deletion Successful',
        message: `The ${deleteInfo.entityType} "${deleteInfo.entityName}" has been permanently deleted. The ticket will be automatically resolved.`
      });

      // Refresh the ticket data (the backend will auto-resolve the ticket)
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting entity:', error);

      let errorMessage = `Failed to delete ${deleteInfo.entityType}. Please try again or contact support.`;

      // Handle specific error cases
      if (error.response?.status === 404) {
        errorMessage = `The ${deleteInfo.entityType} "${deleteInfo.entityName}" (ID: ${deleteInfo.entityId}) was not found in the database. It may have already been deleted.`;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setNotification({
        show: true,
        type: 'error',
        title: 'Deletion Failed',
        message: errorMessage
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAssignToSelf = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/assign`,
        {
          assigned_to: userInfo.user_id,
          assigned_by: userInfo.user_id
        },
        auth
      );

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Ticket Assigned Successfully',
        message: `Ticket ${ticket.ticket_number} has been assigned to you. You can now work on resolving this ticket.`
      });

      onUpdate();
    } catch (error) {
      console.error('Error assigning ticket:', error);

      // Show error notification
      setNotification({
        show: true,
        type: 'error',
        title: 'Assignment Failed',
        message: 'Failed to assign the ticket to yourself. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => (
    <span style={{
      backgroundColor: statusColors[status] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {status.replace('_', ' ')}
    </span>
  );

  const getPriorityBadge = (priority) => (
    <span style={{
      backgroundColor: priorityColors[priority] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {priority}
    </span>
  );

  // Helper function to check if this is a delete request ticket
  const isDeleteRequestTicket = () => {
    return ticket.title && ticket.title.startsWith('Delete Request -');
  };

  // Helper function to extract entity information from delete request ticket
  const parseDeleteRequestInfo = () => {
    if (!isDeleteRequestTicket()) return null;

    const description = ticket.description || '';

    // Extract entity type from title
    const entityType = ticket.title.includes('Trader:') ? 'trader' :
                      ticket.title.includes('Group:') ? 'group' : null;

    // Extract entity ID from description
    const idMatch = description.match(new RegExp(`${entityType === 'trader' ? 'Trader' : 'Group'} ID: (\\d+)`));
    const entityId = idMatch ? parseInt(idMatch[1]) : null;

    // Extract entity name from title
    const nameMatch = ticket.title.match(/Delete Request - (?:Trader|Group): (.+)/);
    const entityName = nameMatch ? nameMatch[1] : 'Unknown';

    return {
      entityType,
      entityId,
      entityName
    };
  };





  if (!ticket) return null;

  return (
    <>
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          borderBottom: '1px solid #dee2e6',
          paddingBottom: '1rem'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FaTicketAlt color="#3498db" />
              Ticket #{ticket.ticket_number}
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#7f8c8d' }}>
              Created by {ticket.creator_name} on {formatDate(ticket.created_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Ticket Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '0.25rem'
        }}>
          <div>
            <strong>Status:</strong><br />
            {getStatusBadge(ticket.status)}
          </div>
          <div>
            <strong>Priority:</strong><br />
            {getPriorityBadge(ticket.priority)}
          </div>
          <div>
            <strong>Category:</strong><br />
            {ticket.category.replace('_', ' ')}
          </div>
          <div>
            <strong>Assigned To:</strong><br />
            {ticket.assignee_name || <span style={{ color: '#e74c3c' }}>Unassigned</span>}
          </div>
        </div>

        {/* Title and Description */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>{ticket.title}</h3>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '0.25rem',
            border: '1px solid #dee2e6'
          }}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
          </div>
        </div>

        {/* Resolution Notes */}
        {ticket.resolution_notes && (
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#27ae60' }}>Resolution</h4>
            <div style={{
              backgroundColor: '#f0f9f0',
              padding: '1rem',
              borderRadius: '0.25rem',
              border: '1px solid #27ae60'
            }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ticket.resolution_notes}</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
                Resolved by {ticket.resolver_name} on {formatDate(ticket.resolved_at)}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '0.25rem',
            border: '1px solid #ffeaa7'
          }}>
            {!ticket.assigned_to && (
              <button
                onClick={handleAssignToSelf}
                disabled={loading}
                style={{
                  backgroundColor: '#f39c12',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaUserCheck />
                Assign to Me
              </button>
            )}
            {(ticket.assigned_to === userInfo.user_id || !ticket.assigned_to || userInfo.role_name === 'mmda') && (
              <button
                onClick={() => setShowResolutionForm(true)}
                style={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaCheck />
                Resolve Ticket
              </button>
            )}
            {/* Delete Request Button for Delete Request Tickets */}
            {isDeleteRequestTicket() && parseDeleteRequestInfo()?.entityId && entityExists === true && (
              <button
                onClick={handleDeleteEntity}
                disabled={deleteLoading}
                style={{
                  backgroundColor: deleteLoading ? '#95a5a6' : '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: deleteLoading ? 0.7 : 1
                }}
                title={`Delete ${parseDeleteRequestInfo()?.entityType || 'entity'} from database`}
              >
                <FaTrash />
                {deleteLoading ? 'Deleting...' : `Delete ${parseDeleteRequestInfo()?.entityType === 'trader' ? 'Trader' : 'Group'}`}
              </button>
            )}
            {/* Show message if entity doesn't exist */}
            {isDeleteRequestTicket() && parseDeleteRequestInfo()?.entityId && entityExists === false && (
              <div style={{
                backgroundColor: '#f39c12',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '0.25rem',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                ⚠️ The {parseDeleteRequestInfo()?.entityType} "{parseDeleteRequestInfo()?.entityName}" (ID: {parseDeleteRequestInfo()?.entityId}) no longer exists in the database.
              </div>
            )}

          </div>
        )}

        {/* Resolution Form */}
        {showResolutionForm && (
          <div style={{
            marginBottom: '2rem',
            padding: '1rem',
            backgroundColor: '#f0f9f0',
            borderRadius: '0.25rem',
            border: '1px solid #27ae60'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#27ae60' }}>Resolve Ticket</h4>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Describe how this issue was resolved..."
              rows={4}
              style={{
                width: '95%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.25rem',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleResolveTicket}
                disabled={loading}
                style={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                {loading ? 'Resolving...' : 'Resolve'}
              </button>
              <button
                onClick={() => setShowResolutionForm(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaComments />
            Comments ({comments.length})
          </h4>

          {/* Comments List */}
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '0.25rem',
            marginBottom: '1rem'
          }}>
            {comments.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                No comments yet
              </div>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.comment_id}
                  style={{
                    padding: '1rem',
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: comment.is_internal ? '#fff3cd' : 'white'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>{comment.user_name}</strong>
                      <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        ({comment.role_name})
                      </span>
                      {comment.is_internal && (
                        <span style={{
                          backgroundColor: '#f39c12',
                          color: 'white',
                          padding: '0.125rem 0.25rem',
                          borderRadius: '0.125rem',
                          fontSize: '0.7rem'
                        }}>
                          INTERNAL
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{comment.comment}</p>
                </div>
              ))
            )}
          </div>

          {/* Add Comment Form */}
          <form onSubmit={handleAddComment}>
            <div style={{ marginBottom: '1rem' }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                style={{
                  width: '95%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                Internal note (only visible to MMDA staff)
              </label>
              <button
                type="submit"
                disabled={loading || !newComment.trim()}
                style={{
                  backgroundColor: loading || !newComment.trim() ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaPaperPlane />
                {loading ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {/* Assignment Notification Modal */}
    {notification.show && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{
              margin: 0,
              color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {notification.type === 'success' ? '✅' : '❌'}
              {notification.title}
            </h3>
            <button
              onClick={handleNotificationClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#7f8c8d'
              }}
            >
              ×
            </button>
          </div>

          {/* Message */}
          <p style={{
            margin: '0 0 2rem 0',
            color: '#2c3e50',
            lineHeight: '1.6'
          }}>
            {notification.message}
          </p>

          {/* Close Button */}
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={handleNotificationClose}
              style={{
                backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default TicketDetailsModal;
